import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/aklive/Header";
import { Footer } from "@/components/aklive/Footer";
import { MaskedLines, Reveal } from "@/components/aklive/motion";
import { ServicesIndex } from "@/components/aklive/services/ServicesIndex";

const TERMS = [
  {
    term: "Content pillars",
    def: "Content pillars are the main subjects a business talks about regularly. They keep communication focused and help a team plan useful content instead of creating random posts.",
  },
  {
    term: "Wayfinding",
    def: "Wayfinding means signs, maps and visual cues that help attendees move through an event without confusion.",
  },
  {
    term: "UX design",
    def: "UX means User Experience. It makes information easier to find, understand and act on. Good UX removes friction.",
  },
  {
    term: "Design retainers",
    def: "Ongoing support for growing startups, event companies, marketing teams and founder-led businesses that need regular design help without hiring a complete in-house team.",
  },
];

export default function Services() {
  useEffect(() => {
    document.title = "AKLIVE — Services";
  }, []);

  return (
    <div className="bg-paper text-left text-ink" data-testid="services-page">
      <Header />
      <main id="main" className="overflow-x-clip">
        <section className="pt-28 md:pt-36" data-testid="services-hero">
          <div className="container-ak">
            <h1 className="font-display text-[7.2vw] font-bold leading-[1.04] tracking-[-0.02em] text-ink sm:text-4xl md:text-5xl lg:text-6xl">
              <MaskedLines
                lines={[
                  "Design That Helps",
                  "Businesses Look Bigger,",
                  "Sell Better",
                  <span key="l4">
                    and Grow Faster<span className="text-ember">.</span>
                  </span>,
                ]}
              />
            </h1>
            <Reveal delay={0.2} className="mt-8 max-w-[620px] space-y-4 md:mt-10">
              <p className="text-base leading-relaxed text-ink2 md:text-lg">
                Whether launching a company, raising funds, introducing a product or standing out in
                a crowded market, how a business presents itself directly influences trust,
                understanding and action.
              </p>
              <p className="text-base leading-relaxed text-ink2 md:text-lg">
                At AKLIVE, we create brands, presentations, websites, packaging and communication
                systems that help businesses communicate clearly and grow confidently.
              </p>
            </Reveal>
            <Reveal delay={0.3} className="mt-10 md:mt-12">
              <Link to="/work" data-testid="services-hero-work-btn" className="btn-primary h-14 px-8 text-base">
                View Our Work
              </Link>
            </Reveal>
          </div>
        </section>

        <ServicesIndex />

        <section className="py-24 md:py-40" data-testid="plain-language-section">
          <div className="container-ak">
            <div className="grid gap-12 lg:grid-cols-12 lg:gap-8">
              <div className="lg:col-span-4">
                <h2 className="font-display text-3xl font-bold leading-[1.05] tracking-tight text-ink sm:text-4xl lg:text-5xl">
                  <MaskedLines lines={["Plain", <span key="l2">language<span className="text-ember">.</span></span>]} />
                </h2>
                <Reveal delay={0.15} className="mt-5">
                  <p className="max-w-[380px] text-base leading-relaxed text-ink2">
                    Unfamiliar terms, explained immediately.
                  </p>
                </Reveal>
              </div>
              <div className="lg:col-span-7 lg:col-start-6">
                <Reveal>
                  <dl className="border-t border-line">
                    {TERMS.map((t) => (
                      <div key={t.term} className="grid gap-2 border-b border-line py-7 sm:grid-cols-12 sm:gap-6 md:py-9">
                        <dt className="font-display text-xl font-bold tracking-tight text-ink sm:col-span-4 md:text-2xl">
                          {t.term}
                        </dt>
                        <dd className="m-0 text-base leading-relaxed text-ink2 sm:col-span-8">{t.def}</dd>
                      </div>
                    ))}
                  </dl>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-24 md:pb-40" data-testid="services-cta-section">
          <div className="container-ak">
            <h2 className="max-w-[24ch] font-display text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              <MaskedLines
                lines={[
                  "Good Design Shouldn't",
                  "Just Look Better.",
                  <span key="l3" className="text-ember">
                    It Should Help Your Business Perform Better.
                  </span>,
                ]}
              />
            </h2>
            <Reveal delay={0.25} className="mt-10 flex flex-col items-start gap-6 sm:flex-row sm:items-center md:mt-14">
              <Link to="/contact" data-testid="services-cta-start-btn" className="btn-primary h-14 px-8 text-base">
                Start a Project
              </Link>
              <Link to="/work" data-testid="services-cta-work-btn" className="btn-secondary h-14 px-8 text-base">
                View Our Work
              </Link>
            </Reveal>
            <Reveal delay={0.35} className="mt-8">
              <a
                href="mailto:connect@aklive.studio"
                data-testid="services-email-link"
                className="link-underline inline-flex min-h-[44px] items-center font-display text-lg font-bold tracking-tight text-ink hover:text-ember"
              >
                connect@aklive.studio
              </a>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
